import { Component, OnInit, OnDestroy } from '@angular/core';
import { FooterComponent } from '../../shared/footer/footer.component';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { NavigationEnd, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { EmployeeDataService } from '../../shared/services/employee-data.service';
import { UrlReferenceService } from '../../shared/services/url-reference.service';
import { ToastrService } from 'ngx-toastr';
import { FormsModule } from '@angular/forms';
import { UploadResponse } from '../../models/employee.data.interface';
import { CompanyService } from '../../shared/services/company.service';
import { QuotaTriggerService } from '../../shared/services/quota-trigger.service';
import { TranslateModule } from '@ngx-translate/core';
import { AuthService } from '../../shared/services/auth.service';
import { filter, Subscription, take } from 'rxjs';
import { LoaderComponent } from '../../components/loader/loader.component';
import { StepperComponent } from '../../shared/stepper/stepper.component';
import { ChangeDetectorRef } from '@angular/core';
import { HeaderComponent } from "../../shared/header/header.component";

@Component({
  selector: 'app-import-employees',
  imports: [
    FooterComponent,
    CommonModule,
    FormsModule,
    LoaderComponent,
    TranslateModule,
    StepperComponent,
    HeaderComponent
],
  templateUrl: './import-employees.component.html',
  styleUrl: './import-employees.component.scss',
})
export class ImportEmployeesComponent implements OnInit, OnDestroy {
  // Member form fields
  memberType: 'Employee' | 'Dependent' = 'Employee';
  firstName: string = '';
  lastName: string = '';
  nameAr: string = '';
  memberId: string = '';
  employeeId: string = '';
  gender: string = '';
  dateOfBirth: string = '';
  jobTitle: string = '';
  mobileNumber: string = '';
  selectedPlan: string = 'BASIC';
  isMarried: boolean = false;

  insurancePlans: string[] = ['VIP', 'A', 'B', 'C', 'BASIC'];

  selectedFile!: File;
  private initFile!: File;
  showErrorPopup: boolean = false;
  errorMessage: string = '';
  isLoading: boolean = false;
  isDashboard: boolean = false;
  employeesWithDependents: any[] = [];

  company_id: string = '';
  currentUserId: string = '';
  currentUserRoles: string[] = [];

  private routerSubscription!: Subscription;

  constructor(
    public employeeDataService: EmployeeDataService,
    private http: HttpClient,
    private router: Router,
    private urlReferenceSevice: UrlReferenceService,
    private toastr: ToastrService,
    private companyService: CompanyService,
    private quotaTriggerService: QuotaTriggerService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.initializeComponent();
  }

  ngOnDestroy(): void {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  private initializeComponent(): void {
    this.routerSubscription = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => {
        this.isDashboard = (e.urlAfterRedirects || e.url).includes(
          '/dashboard'
        );
      });

    this.isDashboard = this.router.url.includes('/dashboard');

    this.loadCompanyData();

    this.initFile = this.employeeDataService.selectedFile;
    this.selectedFile = this.employeeDataService.selectedFile;

    this.checkAuthentication();

    this.getEmployeeData();
  }

  private loadCompanyData(): void {
    const applicationData = this.companyService.getApplicationData();

    if (applicationData.companyDetails) {
      const company = applicationData.companyDetails;
      this.company_id = company.id?.toString() || '';
      console.log('Loaded company ID from companyDetails:', this.company_id);
      console.log('Full company details:', company);
    } else if (applicationData.companyData) {
      const company = applicationData.companyData;
      this.company_id = company.id?.toString() || '';
      console.log('Loaded company ID from companyData:', this.company_id);
      console.log('Full company data:', company);
    }
  }

  private checkAuthentication(): void {
    this.authService.isAuthenticated$
      .pipe(
        filter((authenticated: any) => authenticated),
        take(1)
      )
      .subscribe({
        next: () => {
          this.loadCurrentUserInfo();
        },
        error: (error) => {
          console.error('Authentication failed:', error);
          this.router.navigate(['/login']);
        },
      });
  }

  private async loadCurrentUserInfo(): Promise<void> {
    try {
      this.isLoading = true;
      await this.authService.getToken();
      await this.authService.getUserInfo();

      this.currentUserId = this.authService.keycloakInstance.subject!;
      this.currentUserRoles = this.authService.getRoles();
    } catch (error) {
      console.error('User info error:', error);

      const fromGetQuote = localStorage.getItem('from-get-quote');
      if (fromGetQuote !== 'true') {
        this.toastr.error('Please login to continue');
        this.router.navigate(['/login']);
      }
    } finally {
      this.isLoading = false;
    }
  }

  clearInputs(): void {
    this.firstName = '';
    this.lastName = '';
    this.nameAr = '';
    this.memberId = '';
    this.employeeId = '';
    this.gender = '';
    this.dateOfBirth = '';
    this.jobTitle = '';
    this.mobileNumber = '';
    this.memberType = 'Employee';
    this.selectedPlan = 'Basic';
    this.isMarried = false;
  }

  private validateInputs(): boolean {
    if (!this.memberId) {
      this.toastr.error('Please enter member ID');
      return false;
    }

    if (this.memberType === 'Dependent' && !this.employeeId) {
      this.toastr.error('Please enter sponsor ID for dependent');
      return false;
    }

    if (!this.dateOfBirth) {
      this.toastr.error('Please enter Date of Birth');
      return false;
    }

    if (!this.mobileNumber) {
      this.toastr.error('Please enter Mobile Number');
      return false;
    }

    if (!/^\d{9,10}$/.test(this.mobileNumber)) {
      this.toastr.error('Mobile Number must be 9 or 10 digits');
      return false;
    }

    if (!this.gender) {
      this.toastr.error('Please select a gender');
      return false;
    }

    return true;
  }

  onSaveMember(): void {
    if (!this.validateInputs()) {
      return;
    }

    const payload: any = {
      memberType: this.memberType,
      firstNameEn: this.firstName,
      lastNameEn: this.lastName,
      nameAr: this.nameAr,
      insurancePlan: this.selectedPlan,
      employeeId: this.employeeId,
      memberId: this.memberId,
      mobileNumber: this.mobileNumber,
      dateOfBirth: this.dateOfBirth,
      isMarried: this.isMarried,
      gender: this.gender,
    };

    if (this.memberType === 'Employee') {
      payload.jobTitle = this.jobTitle;
    }

    console.log('Saving member with payload:', payload);

    this.employeeDataService.saveMember(this.company_id, payload).subscribe({
      next: (response) => {
        console.log('Member saved successfully, response:', response);
        this.toastr.success('Member saved successfully');

        if (this.company_id) {
          this.quotaTriggerService.markInsuranceClassUpdated(this.company_id);
        }

        this.getEmployeeData();
        this.clearInputs();
      },
      error: (error: any) => {
        if (error.status === 400) {
          this.toastr.error(
            'A member with this ID already exists. Please use a different member ID.'
          );
        } else {
          this.toastr.error('Failed to save member.');
        }
      },
    });
  }

  downloadTemplate(): void {
    const url = this.urlReferenceSevice.downloadSampleExcelUrl;

    this.http.get(url, { responseType: 'blob' }).subscribe({
      next: (blob) => {
        const urlBlob = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = urlBlob;
        link.download = 'sample.xlsx';
        link.click();
        window.URL.revokeObjectURL(urlBlob);
      },
      error: (err) => {
        console.error('Download failed:', err);
        this.toastr.error('Failed to download template');
      },
    });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedFile = input.files[0];
      this.employeeDataService.selectedFile = this.selectedFile;
      this.toastr.success('File selected: ' + this.selectedFile.name);

      // Auto-upload the file immediately after selection
      this.handleFileUpload();
    }
  }

  onRemoveFile(fileInputRef?: HTMLInputElement): void {
    this.selectedFile = null as any;
    this.employeeDataService.selectedFile = null as any;

    // Clear the file input
    if (fileInputRef) {
      fileInputRef.value = '';
    } else {
      const fileInput = document.querySelector(
        'input[type="file"]'
      ) as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }
    }

    this.toastr.info('File removed');
  }
  onDeleteMember(member: any): void {
    if (
      !confirm(
        `Are you sure you want to delete ${member.firstNameEn} ${member.lastNameEn}?`
      )
    ) {
      return;
    }

    const memberType = member.type === 'Employee' ? 'employee' : 'dependent';
    const memberId = member.id;

    this.employeeDataService
      .deleteSingleMember(this.company_id, memberType, memberId)
      .subscribe({
        next: () => {
          this.toastr.success('Member deleted successfully');
          this.getEmployeeData(); // Refresh the list
        },
        error: (err) => {
          console.error('Failed to delete member:', err);
          this.toastr.error('Failed to delete member');
        },
      });
  }

  private getEmployeeData(): void {
    this.employeeDataService.getMembers(this.company_id, 0, 100, null).subscribe({
      next: (data) => {
        this.employeeDataService.employeeData = data;
        this.processEmployeesWithDependents(data);

        const employeeCount = this.employeesWithDependents.length;
        const dependentCount = this.employeesWithDependents.reduce(
          (acc, emp) => acc + (emp.dependentList?.length || 0),
          0
        );

        let maleCount = 0;
        let femaleCount = 0;

        this.employeesWithDependents.forEach((emp) => {
          if (emp.gender?.toLowerCase() === 'male') {
            maleCount++;
          } else if (emp.gender?.toLowerCase() === 'female') {
            femaleCount++;
          }

          (emp.dependentList || []).forEach((dep: any) => {
            if (dep.gender?.toLowerCase() === 'male') {
              maleCount++;
            } else if (dep.gender?.toLowerCase() === 'female') {
              femaleCount++;
            }
          });
        });

        this.companyService.setEmployeeCount(employeeCount);
        this.companyService.setDependentCount(dependentCount);
        this.companyService.setMaleCount(maleCount);
        this.companyService.setFemaleCount(femaleCount);

        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Failed to fetch employee data:', err);
        this.toastr.error('Failed to fetch employee data');
      },
    });
  }

  private processEmployeesWithDependents(data: any): void {
    if (!data || !data.content || !data.content.employee) {
      this.employeesWithDependents = [];
      return;
    }

    const employees = data.content.employee || [];

    console.log('Processing employees:', employees);

    // Process employees and transform their nested dependentList
    this.employeesWithDependents = employees.map((employee: any) => {
      // Transform dependents from dependentList
      const transformedDependents = (employee.dependentList || []).map(
        (dependent: any) => ({
          ...dependent,
          type: 'Dependent',
          initials: this.getInitials(
            dependent.firstNameEn || 'D',
            dependent.lastNameEn || 'P'
          ),
        })
      );

      console.log(
        `Employee ${employee.idNumber} has ${transformedDependents.length} dependent(s)`
      );

      return {
        ...employee,
        type: 'Employee',
        initials: this.getInitials(
          employee.firstNameEn || employee.nameArabic?.split(' ')[0] || 'U',
          employee.lastNameEn ||
            employee.nameArabic?.split(' ').slice(1).join(' ') ||
            'N'
        ),
        dependents: transformedDependents,
      };
    });

    console.log(
      'Final processed employees with dependents:',
      this.employeesWithDependents
    );
  }

  private getInitials(firstName: string, lastName: string): string {
    const first = firstName?.charAt(0)?.toUpperCase() || '';
    const last = lastName?.charAt(0)?.toUpperCase() || '';
    return first + last || 'U';
  }

  get totalMembersCount(): number {
    const employeeCount = this.employeesWithDependents.length;
    const dependentCount = this.employeesWithDependents.reduce(
      (acc, emp) => acc + (emp.dependents?.length || 0),
      0
    );
    return employeeCount + dependentCount;
  }

  onNextClick(): void {
    this.employeeDataService.getEmployees(this.company_id).subscribe({
      next: (data) => {
        this.employeeDataService.employeeData = data;

        if (
          !this.employeeDataService.isEmployeeDataAvailable() &&
          !this.selectedFile
        ) {
          this.toastr.warning(
            'Please add members or upload a file to continue'
          );
          return;
        }

        if (this.selectedFile && this.selectedFile !== this.initFile) {
          this.handleFileUpload();
        } else {
          this.navigateToHealthDeclaration();
        }
      },
      error: (err) => {
        console.error('Failed to fetch employee data:', err);
        this.toastr.error('Failed to fetch employee data');
      },
    });
  }

  private handleFileUpload(): void {
    if (!this.selectedFile) {
      this.toastr.error('Please select a file to upload');
      return;
    }

    this.isLoading = true;
    this.employeeDataService
      .uploadEmployeeFile(this.company_id, this.selectedFile)
      .subscribe({
        next: (response: HttpResponse<UploadResponse>) => {
          this.isLoading = false;
          this.handleUploadResponse(response);
        },
        error: (err) => {
          this.isLoading = false;
          this.displayError('Upload failed: ' + err.message);
        },
      });
  }

  private handleUploadResponse(response: HttpResponse<UploadResponse>): void {
    if (response.status === 200) {
      if (response.body?.errors && response.body.errors.length > 0) {
        this.displayError(response.body.errors.join('<br>'));
      } else {
        this.toastr.success('File uploaded successfully');

        // Refresh employee data from the server to get the latest data
        this.getEmployeeData();
      }
    } else {
      this.displayError(`Upload failed with status: ${response.status}`);
    }
  }

  private navigateToHealthDeclaration(): void {
    this.companyService.markStepCompleted(2);

    const route = '/health-declaration';
    this.router.navigate([route], {
      state: { employeeData: this.employeeDataService.employeeData },
    });
  }

  handleBack(): void {
    window.history.back();
  }

  displayError(message: string): void {
    this.errorMessage = message;
    this.showErrorPopup = true;
  }

  closeErrorPopup(): void {
    this.showErrorPopup = false;
    this.errorMessage = '';
  }
}
